import React from "react";
import debounce from "lodash/debounce";
import styled from "styled-components";
import { useMutation, queryCache } from "react-query";
import {
  Modal,
  Button,
  Input,
  DatePicker,
  Select,
  Spin,
  Typography,
  Empty,
} from "antd";
import { useForm, Controller } from "react-hook-form";
import moment from "moment";
import useSearchUser from "hooks/api/user/useSearchUser";
import { NotificationStateContext } from "context/UI/Notification/NotificationState";
import { apiAxios } from "services/HttpServices";
import { removeEmptyFields } from "lib/utils";

const { Option } = Select;
const { Text } = Typography;

const updateTask = async ({ todoId, taskId, task }) => {
  try {
    const res = await apiAxios.put(`/todos/${todoId}/tasks/${taskId}`, task);
    return res.data.data;
  } catch (e) {
    throw e.response.data.message;
  }
};

export const EditTaskModal = (props) => {
  const { modalVisible, selectedTask, handleCloseModal, jobId, todoId } = props;
  const { control, handleSubmit, reset, setValue, formState } = useForm();
  const { isDirty } = formState;
  const [keyword, setKeyword] = React.useState("");
  const [error, setError] = React.useState("");
  const { data, status: searchUserStatus } = useSearchUser(keyword);
  const {
    state: notificationState,
    setLoading,
    finishLoading,
  } = React.useContext(NotificationStateContext);

  const titleRef = React.useRef();

  const [mutateUpdate] = useMutation(updateTask, {
    onSuccess: (taskData, variables) => {
      handleCloseModal();
      queryCache.setQueryData(
        ["job-detail", variables.jobId.toString()],
        function (oldData) {
          const taskIndex = oldData.todo.tasks.findIndex(
            (task) => task.id === variables.taskId
          );
          oldData.todo.tasks[taskIndex] = taskData;
          return oldData;
        }
      );
    },
    onError: (e) => {
      setError(e);
    },
  });

  React.useEffect(() => {
    if (titleRef.current) {
      titleRef.current.focus();
    }
    if (selectedTask) {
      reset({
        title: selectedTask.title,
        due_date:
          selectedTask.due_date && selectedTask.due_date !== null
            ? moment.utc(selectedTask?.due_date) || ""
            : "",
        asignees: selectedTask.asignees
          ? formatDefaultValue(selectedTask.asignees)
          : [],
      });
    } else {
      reset({ title: "", due_date: undefined, asignees: [] });
    }
  }, [selectedTask]);

  const handleSubmitData = async (data, option) => {
    setError("");
    const dataToSubmit = {
      title: data.title,
      due_date: data.due_date ? data.due_date.format() : "",
    };

    if (data.asignees && data.asignees.length > 0) {
      dataToSubmit["asignees"] = data.asignees.map((asignee) => ({
        id: parseInt(asignee.value),
      }));
    }

    removeEmptyFields(dataToSubmit);
    setLoading("updateTask");
    try {
      await mutateUpdate({
        todoId,
        taskId: selectedTask.id,
        jobId,
        task: dataToSubmit,
      });
      finishLoading("updateTask");
    } catch (error) {
      finishLoading("updateTask");
      console.log(error);
    }

    return dataToSubmit;
  };

  const searchUserByKeyword = (keyword) => {
    setKeyword(keyword);
  };

  const formatData = (resData) => {
    const newData = resData?.map((d) => ({
      key: d.id,
      value: d.id,
      text: d.name,
    }));

    return newData;
  };

  const formatDefaultValue = (resData) => {
    const newData = resData?.map((d) => ({
      key: d.id.toString(),
      label: d.name,
      value: d.id.toString(),
    }));
    return newData;
  };

  return (
    <Modal
      visible={modalVisible}
      title={`Update ${selectedTask.title}`}
      onOk={handleSubmit(handleSubmitData)}
      onCancel={() => {
        handleCloseModal();
        setError("");
        setKeyword("");
      }}
      footer={[
        <Button
          key="back"
          onClick={() => {
            handleCloseModal();
            setError("");
            setKeyword("");
          }}
        >
          Return
        </Button>,
        <Button
          key="submit"
          type="primary"
          disabled={!isDirty}
          onClick={handleSubmit(handleSubmitData)}
          loading={notificationState.loading.updateTask}
        >
          Update
        </Button>,
      ]}
    >
      {error && <ErrorContainer>{error}</ErrorContainer>}

      <form onSubmit={handleSubmit(handleSubmitData)}>
        <StyledInput>
          <Text className="title">Title</Text>
          <Controller
            as={
              <Input
                ref={(input) => {
                  titleRef.current = input;
                }}
              />
            }
            name="title"
            placeholder="Task title"
            control={control}
            defaultValue={selectedTask ? selectedTask.title : ""}
          />
        </StyledInput>

        <StyledInput>
          <Text className="title">Due date</Text>
          <Controller
            as={DatePicker}
            name="due_date"
            control={control}
            defaultValue={
              selectedTask
                ? selectedTask.due_date !== null
                  ? moment.utc(selectedTask.due_date)
                  : ""
                : ""
            }
            style={{ width: "100%" }}
          />
        </StyledInput>

        <StyledInput>
          <Text className="title">Asign staff</Text>
          <Controller
            name="asignees"
            control={control}
            render={({ value }) => {
              return (
                <Select
                  mode="multiple"
                  placeholder="Assign users"
                  labelInValue
                  filterOption={false}
                  value={value}
                  defaultValue={
                    selectedTask ? selectedTask.asignees : undefined
                  }
                  onSearch={debounce(searchUserByKeyword, 800)}
                  notFoundContent={
                    searchUserStatus === "loading" ? (
                      <Spin size="small" />
                    ) : (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    )
                  }
                  onChange={(e) => {
                    setKeyword("");
                    setValue("asignees", e, {
                      shouldDirty: true,
                    });
                  }}
                  style={{ width: "100%" }}
                >
                  {formatData(data)?.map((d) => (
                    <Option key={d.value}>{d.text}</Option>
                  ))}
                </Select>
              );
            }}
          />
        </StyledInput>
        <Button htmlType="submit" hidden>
          Submit
        </Button>
      </form>
    </Modal>
  );
};

const StyledInput = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: ${(props) => props.theme.spacing[2]};

  .title {
    font-size: 0.8rem;
    color: ${(props) => props.theme.colors.grey[600]};
  }
`;

const ErrorContainer = styled.div`
  padding: ${(props) => props.theme.spacing[2]};
  background-color: ${(props) => props.theme.colors.red[100]};
  border: 1px solid ${(props) => props.theme.colors[700]};
  font-weight: 600;
  color: ${(props) => props.theme.colors.red[500]};
  margin-bottom: ${(props) => props.theme.spacing[2]};
`;

export default EditTaskModal;
